import React, { useEffect, useState, useCallback } from 'react';
import { X, Sparkles, ArrowRight } from 'lucide-react';
import { useBooking } from '../context/BookingContext';

const STORAGE_KEY = 'akira_welcome_popup_seen_v1';
const SHOW_DELAY_MS = 4500;

const WelcomePopup = () => {
  const [open, setOpen] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    let seen = false;
    try {
      seen = window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      /* ignore */
    }
    if (seen) return undefined;
    const timer = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-popup-title"
      data-testid="welcome-popup"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close welcome offer"
        onClick={close}
        data-testid="welcome-popup-backdrop"
        className="absolute inset-0 bg-stone-950/70 backdrop-blur-sm cursor-default"
      />

      {/* Card */}
      <div className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-stone-50 shadow-2xl animate-in fade-in zoom-in-95 duration-300">
        {/* Decorative header */}
        <div className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-rose-900 px-8 pt-10 pb-14 text-white overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-rose-400/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-rose-500/20 blur-3xl" />
          <button
            type="button"
            aria-label="Close welcome offer"
            onClick={close}
            data-testid="welcome-popup-close"
            className="absolute top-4 right-4 p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/25 px-3 py-1 text-[10px] uppercase tracking-[0.35em] text-rose-200">
              <Sparkles className="w-3.5 h-3.5" />
              Welcome Offer
            </div>
            <h2
              id="welcome-popup-title"
              className="mt-5 font-serif text-4xl leading-tight text-white sm:text-5xl"
            >
              20% off your <span className="italic text-rose-200">first visit</span>
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="relative -mt-6 rounded-t-3xl bg-stone-50 px-8 pt-8 pb-8">
          <p className="text-stone-600 leading-relaxed">
            A little welcome from our team — first-time patients enjoy{' '}
            <strong className="text-stone-900">20% off any treatment</strong>{' '}
            when you book your consultation with Akira Signature Care.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              onClick={() => {
                openBooking('First-Time 20% Offer');
                close();
              }}
              data-testid="welcome-popup-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-rose-400 hover:bg-rose-500 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.2em] text-white transition-colors"
            >
              Book My Consultation
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={close}
              data-testid="welcome-popup-dismiss"
              className="text-xs uppercase tracking-[0.3em] text-stone-400 hover:text-stone-700 transition-colors"
            >
              Maybe later
            </button>
          </div>
          <p className="mt-6 text-[11px] text-stone-400 leading-relaxed">
            Offer valid for new patients only. Mention this welcome offer at
            booking. Cannot be combined with other promotions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomePopup;
